import { useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileDown, Loader2, CheckCircle } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fINR = (val) => {
  if (!val) return "0";
  if (val >= 10000000) return `INR ${(val / 10000000).toFixed(2)} Cr`;
  if (val >= 100000) return `INR ${(val / 100000).toFixed(1)} L`;
  if (val >= 1000) return `INR ${(val / 1000).toFixed(0)}K`;
  return `INR ${Math.round(val).toLocaleString()}`;
};
const fNum = (v) => v?.toLocaleString() || "0";

export default function ExportPDF() {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const generatePDF = async () => {
    setLoading(true);
    setDone(false);
    try {
      const [reportRes, ltvRes, adsRes, cacRes] = await Promise.all([
        axios.get(`${API}/report/data`),
        axios.get(`${API}/ltv/analysis`),
        axios.get(`${API}/financial/ads-traffic`),
        axios.get(`${API}/cohort/cac-analysis`),
      ]);
      const data = reportRes.data;
      const ltvFull = ltvRes.data;
      const adsData = adsRes.data;
      const cacData = cacRes.data;
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      const M = 16; // margin
      const CW = W - M * 2;

      // --- COLORS ---
      const C = {
        navy: [2, 6, 23], slate900: [15, 23, 42], slate800: [30, 41, 59],
        blue: [59, 130, 246], emerald: [16, 185, 129], amber: [245, 158, 11],
        violet: [139, 92, 246], rose: [244, 63, 94],
        white: [248, 250, 252], gray: [148, 163, 184], lightGray: [226, 232, 240],
        amberBg: [30, 22, 5], amberBorder: [120, 80, 10], amberText: [252, 211, 77],
      };

      // --- HELPERS ---
      const pageNum = { current: 0 };
      const addPage = (title, subtitle) => {
        doc.addPage();
        pageNum.current++;
        doc.setFillColor(...C.navy); doc.rect(0, 0, W, H, "F");
        // Top bar
        doc.setFillColor(...C.slate800); doc.rect(0, 0, W, 13, "F");
        doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(...C.gray);
        doc.text("STARANDDAISY  |  INVESTOR COHORT ANALYTICS", M, 8.5);
        doc.text(`Page ${pageNum.current}`, W - M, 8.5, { align: "right" });
        // Title
        doc.setFont("helvetica", "bold"); doc.setFontSize(15); doc.setTextColor(...C.white);
        doc.text(title, M, 26);
        if (subtitle) {
          doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...C.gray);
          doc.text(subtitle, M, 32);
        }
        doc.setDrawColor(...C.slate800); doc.line(M, subtitle ? 35 : 30, W - M, subtitle ? 35 : 30);
        // Footer
        doc.setFontSize(6.5); doc.setTextColor(80, 90, 110);
        doc.text("Confidential  |  Prepared for Investor Review", M, H - 8);
        doc.text(new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long" }), W - M, H - 8, { align: "right" });
        return subtitle ? 40 : 35;
      };

      const kpiBox = (x, y, w, label, value, color = C.blue) => {
        doc.setFillColor(...C.slate900); doc.roundedRect(x, y, w, 20, 1.5, 1.5, "F");
        doc.setFont("helvetica", "normal"); doc.setFontSize(6); doc.setTextColor(...C.gray);
        doc.text(label.toUpperCase(), x + 3, y + 6.5);
        doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(...color);
        doc.text(String(value), x + 3, y + 15.5);
      };

      // Annotation box helper — amber accent
      const annotation = (y, text, maxWidth) => {
        maxWidth = maxWidth || CW;
        doc.setFont("helvetica", "normal"); doc.setFontSize(6.5);
        const lines = doc.splitTextToSize(text, maxWidth - 14);
        const boxH = lines.length * 3.2 + 6;
        doc.setFillColor(...C.amberBg); doc.roundedRect(M, y, maxWidth, boxH, 1.5, 1.5, "F");
        doc.setDrawColor(...C.amberBorder); doc.roundedRect(M, y, maxWidth, boxH, 1.5, 1.5, "S");
        // Amber left accent bar
        doc.setFillColor(...C.amber); doc.rect(M, y, 2, boxH, "F");
        doc.setTextColor(...C.amberText);
        doc.text(lines, M + 7, y + 4.5);
        return y + boxH + 3;
      };

      const tableStyle = {
        styles: { fontSize: 6.5, cellPadding: 2, textColor: [200, 210, 220], fillColor: C.slate900, lineColor: C.slate800, lineWidth: 0.2 },
        headStyles: { fillColor: C.slate800, textColor: C.gray, fontStyle: "bold", fontSize: 6 },
        alternateRowStyles: { fillColor: [10, 18, 35] },
        theme: "grid",
        margin: { left: M, right: M },
      };

      const kpis = data.kpis || {};
      const ltv = data.ltv || {};
      const repBench = ltvFull.repeat_benchmarks || {};
      const actBench = ltvFull.activated_benchmarks || {};
      const segments = ltvFull.segments || {};
      const avgExp = ltvFull.avg_expansion_ratio || 0;

      // ============================
      // PAGE 0: COVER
      // ============================
      doc.setFillColor(...C.navy); doc.rect(0, 0, W, H, "F");
      doc.setFillColor(...C.blue); doc.rect(0, 0, 4, H, "F"); // Accent bar
      doc.setFillColor(...C.blue); doc.rect(0, 0, W, 3, "F"); // Top bar

      doc.setFont("helvetica", "bold"); doc.setFontSize(36); doc.setTextColor(...C.white);
      doc.text("StarAndDaisy", M + 6, 55);
      doc.setFont("helvetica", "normal"); doc.setFontSize(12); doc.setTextColor(...C.gray);
      doc.text("INVESTOR COHORT ANALYTICS REPORT", M + 6, 67);
      doc.setFontSize(9);
      doc.text(`Data Period: ${kpis.date_range?.from || ""} to ${kpis.date_range?.to || ""}`, M + 6, 80);
      doc.text("D2C Website Channel  |  Durable Baby Gear Brand", M + 6, 88);

      // Cover KPI panel
      let cy = 108;
      doc.setFillColor(...C.slate900); doc.roundedRect(M + 6, cy, CW - 6, 55, 2, 2, "F");
      const cMetrics = [
        ["Total Revenue", fINR(kpis.total_revenue), C.emerald],
        ["Customers", fNum(kpis.total_customers), C.blue],
        ["Repeat Rate (365d)", `${kpis.repeat_purchase_rate}%`, C.violet],
        ["Cross-Category", `${kpis.cross_category_rate}%`, C.amber],
      ];
      cMetrics.forEach((m, i) => {
        const mx = M + 12 + i * 43;
        doc.setFont("helvetica", "normal"); doc.setFontSize(6); doc.setTextColor(...C.gray);
        doc.text(m[0].toUpperCase(), mx, cy + 12);
        doc.setFont("helvetica", "bold"); doc.setFontSize(15); doc.setTextColor(...m[2]);
        doc.text(m[1], mx, cy + 26);
      });
      // Second row of cover metrics
      const c2 = [
        ["Repeat Cust. LTV (12M)", fINR(repBench["12m"]), C.emerald],
        ["Repeat LTV Growth 3M→12M", repBench["3m"] > 0 ? `+${(((repBench["12m"]-repBench["3m"])/repBench["3m"])*100).toFixed(0)}%` : "—", C.emerald],
        ["Avg Expansion Ratio", `${avgExp}%`, C.blue],
        ["Total Orders", fNum(kpis.total_orders), C.gray],
      ];
      c2.forEach((m, i) => {
        const mx = M + 12 + i * 43;
        doc.setFont("helvetica", "normal"); doc.setFontSize(6); doc.setTextColor(...C.gray);
        doc.text(m[0].toUpperCase(), mx, cy + 36);
        doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(...m[2]);
        doc.text(m[1], mx, cy + 47);
      });

      // Cover annotation
      cy = 175;
      doc.setFillColor(...C.amberBg); doc.roundedRect(M + 6, cy, CW - 6, 30, 2, 2, "F");
      doc.setDrawColor(...C.amberBorder); doc.roundedRect(M + 6, cy, CW - 6, 30, 2, 2, "S");
      doc.setFillColor(...C.amber); doc.rect(M + 6, cy, 2, 30, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(...C.amberText);
      doc.text("IMPORTANT CONTEXT", M + 14, cy + 6);
      doc.setFont("helvetica", "normal"); doc.setFontSize(6.5);
      const coverNote = doc.splitTextToSize(
        "This report covers D2C website data only (~20% of total brand revenue). Marketplaces (Amazon, Flipkart) do not share customer-level data. " +
        "All repeat/LTV metrics are conservative floors — many customers repurchase via marketplaces (lower sale prices), create new website accounts for discount coupons, " +
        "or initially test on marketplaces due to our no-COD, no-returns website policy. Actual cross-platform customer value is materially higher.",
        CW - 20
      );
      doc.text(coverNote, M + 14, cy + 11);

      // Footer
      doc.setFontSize(7); doc.setTextColor(80, 90, 110);
      doc.text("Confidential  |  Prepared for Investor Review", M + 6, H - 15);
      doc.text(`Generated: ${new Date().toLocaleDateString("en-IN", { year:"numeric", month:"long", day:"numeric" })}`, W - M, H - 15, { align: "right" });

      // ============================
      // PAGE 1: EXECUTIVE SUMMARY
      // ============================
      let y = addPage("Executive Summary", "Key performance indicators and lifecycle analysis");
      const kw = (CW - 6) / 4;
      kpiBox(M, y, kw, "Total Revenue", fINR(kpis.total_revenue), C.emerald);
      kpiBox(M + kw + 2, y, kw, "Total Customers", fNum(kpis.total_customers), C.blue);
      kpiBox(M + (kw+2)*2, y, kw, "Repeat Rate (365d)", `${kpis.repeat_purchase_rate}%`, C.violet);
      kpiBox(M + (kw+2)*3, y, kw, "Cross-Category", `${kpis.cross_category_rate}%`, C.amber);
      y += 25;

      // Funnel
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...C.white);
      doc.text("Customer Lifecycle Funnel", M, y); y += 5;
      (data.funnel || []).forEach((f) => {
        const bW = (f.pct / 100) * (CW - 55);
        doc.setFillColor(...C.slate800); doc.rect(M, y, CW - 55, 5.5, "F");
        doc.setFillColor(...C.blue); doc.rect(M, y, bW, 5.5, "F");
        doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(...C.gray);
        doc.text(f.stage, M + CW - 50, y + 4);
        doc.setTextColor(...C.white);
        doc.text(`${fNum(f.count)} (${f.pct}%)`, M + 2, y + 4);
        y += 8;
      });
      y += 2;

      y = annotation(y,
        "INVESTOR NOTE: The funnel drop from 1+ Orders to Repeat appears steep but is structurally understated. Our product range is exclusively durable baby gear " +
        "(strollers, cribs, walkers, high chairs) — single-use products with zero same-SKU repeat. A \"repeat\" customer buys a completely different product category. " +
        "Additionally, many customers create new accounts for first-time discount coupons, and others repurchase via Amazon/Flipkart where marketplace-subsidized pricing " +
        "is lower during sales. Our no-COD, no-returns website policy also shifts first-time testers to marketplaces."
      );

      // ============================
      // PAGE 2: SEGMENTED LTV
      // ============================
      y = addPage("Lifetime Value Analysis", "Segmented LTV — All vs Repeat vs Activated Customers");

      // Three-segment KPIs
      const sw = (CW - 4) / 3;
      // All
      doc.setFillColor(...C.slate900); doc.roundedRect(M, y, sw, 28, 1.5, 1.5, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(...C.gray);
      doc.text("ALL CUSTOMERS", M + 3, y + 6);
      doc.setFont("helvetica", "normal"); doc.setFontSize(6); doc.text(`${fNum(segments.all_customers)} customers`, M + 3, y + 11);
      doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...C.gray);
      doc.text(`12M: ${fINR(ltv.benchmarks?.["12m"])}`, M + 3, y + 18);
      doc.setFontSize(7); doc.text(`3M→12M: +${ltv.benchmarks?.["3m"] > 0 ? (((ltv.benchmarks["12m"]-ltv.benchmarks["3m"])/ltv.benchmarks["3m"])*100).toFixed(0) : 0}%`, M + 3, y + 24);

      // Repeat
      doc.setFillColor(...C.slate900); doc.roundedRect(M + sw + 2, y, sw, 28, 1.5, 1.5, "F");
      doc.setDrawColor(...C.emerald); doc.roundedRect(M + sw + 2, y, sw, 28, 1.5, 1.5, "S");
      doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(...C.emerald);
      doc.text("REPEAT CUSTOMERS (2+ ORDERS)", M + sw + 5, y + 6);
      doc.setFont("helvetica", "normal"); doc.setFontSize(6); doc.setTextColor(...C.gray);
      doc.text(`${fNum(segments.repeat_customers)} customers`, M + sw + 5, y + 11);
      doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...C.emerald);
      doc.text(`12M: ${fINR(repBench["12m"])}`, M + sw + 5, y + 18);
      const repGr = repBench["3m"] > 0 ? `+${(((repBench["12m"]-repBench["3m"])/repBench["3m"])*100).toFixed(0)}%` : "—";
      doc.setFontSize(8); doc.text(`3M→12M: ${repGr}`, M + sw + 5, y + 24);

      // Activated
      doc.setFillColor(...C.slate900); doc.roundedRect(M + (sw+2)*2, y, sw, 28, 1.5, 1.5, "F");
      doc.setDrawColor(...C.blue); doc.roundedRect(M + (sw+2)*2, y, sw, 28, 1.5, 1.5, "S");
      doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(...C.blue);
      doc.text("ACTIVATED (2+ CATEGORIES)", M + (sw+2)*2 + 3, y + 6);
      doc.setFont("helvetica", "normal"); doc.setFontSize(6); doc.setTextColor(...C.gray);
      doc.text(`${fNum(segments.activated_customers)} customers`, M + (sw+2)*2 + 3, y + 11);
      doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...C.blue);
      doc.text(`12M: ${fINR(actBench["12m"])}`, M + (sw+2)*2 + 3, y + 18);
      const actGr = actBench["3m"] > 0 ? `+${(((actBench["12m"]-actBench["3m"])/actBench["3m"])*100).toFixed(0)}%` : "—";
      doc.setFontSize(8); doc.text(`3M→12M: ${actGr}`, M + (sw+2)*2 + 3, y + 24);

      y += 34;

      y = annotation(y,
        "WHY SEGMENTED LTV MATTERS: The \"All Customers\" LTV appears flat because our products are durable single-use items — a parent who buys a crib will never " +
        "buy another crib. Repeat here means buying a completely different product category, which is a high-intent, high-trust signal. The Repeat Customer segment " +
        `(${fNum(segments.repeat_customers)} customers) shows ${repGr} LTV expansion over 12 months. This is a conservative floor — customers who repurchase via ` +
        "Amazon/Flipkart (common during sales), or create new accounts for discount coupons, are invisible in this data."
      );

      // LTV Benchmarks table
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...C.white);
      doc.text("LTV Benchmark Comparison", M, y); y += 4;
      autoTable(doc, {
        head: [["Segment", "3-Month LTV", "6-Month LTV", "12-Month LTV", "Growth 3M→12M"]],
        body: [
          ["All Customers", fINR(ltv.benchmarks?.["3m"]), fINR(ltv.benchmarks?.["6m"]), fINR(ltv.benchmarks?.["12m"]),
            ltv.benchmarks?.["3m"] > 0 ? `+${(((ltv.benchmarks["12m"]-ltv.benchmarks["3m"])/ltv.benchmarks["3m"])*100).toFixed(0)}%` : "—"],
          ["Repeat (2+ Orders)", fINR(repBench["3m"]), fINR(repBench["6m"]), fINR(repBench["12m"]), repGr],
          ["Activated (2+ Cat.)", fINR(actBench["3m"]), fINR(actBench["6m"]), fINR(actBench["12m"]), actGr],
        ],
        startY: y, ...tableStyle,
      });

      // Expansion ratio
      y = (doc.lastAutoTable?.finalY || doc.previousAutoTable?.finalY || y + 30) + 8;
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...C.white);
      doc.text(`Average Revenue Expansion Ratio: ${avgExp}%`, M, y);
      doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(...C.gray);
      doc.text("(Repeat Revenue / Initial Revenue across mature cohorts — revenue generated at zero incremental acquisition cost)", M, y + 5);

      // ============================
      // PAGE 3: RETENTION (Revenue-Weighted)
      // ============================
      y = addPage("Revenue-Weighted Retention", "Mature cohorts only (9+ months lifecycle)  |  Revenue in M(n) as % of M0 Revenue");
      const retData = (data.retention || []).slice(0, 22);
      if (retData.length > 0) {
        autoTable(doc, {
          head: [["Cohort", "Size", "M0 Rev", "M0", "M1", "M2", "M3", "M4", "M5", "M6", "M9", "M12"]],
          body: retData.map((r) => [
            r.cohort, fNum(r.cohort_size), fINR(r.m0_revenue),
            ...[0, 1, 2, 3, 4, 5, 6, 9, 12].map((m) => r.months?.[String(m)]?.rev_contribution ? `${r.months[String(m)].rev_contribution}%` : "-")
          ]),
          startY: y, ...tableStyle,
          styles: { ...tableStyle.styles, fontSize: 6 },
        });
      }
      y = (doc.lastAutoTable?.finalY || doc.previousAutoTable?.finalY || y + 80) + 4;
      y = annotation(y,
        "METHODOLOGY: Revenue-weighted contribution shows actual revenue impact per cohort month as % of M0 revenue — a more meaningful metric than raw customer count. " +
        "Only cohorts with 9+ months of observable history are shown. Values in M1+ represent organic repeat revenue at zero marginal CAC. " +
        "Note: customers creating new accounts for first-time discount coupons appear as new cohort entries rather than returning in their original cohort — making these values structurally conservative."
      );

      // ============================
      // PAGE 4: REVENUE
      // ============================
      y = addPage("Revenue Analysis", "Monthly revenue breakdown — New vs Returning customers");
      const revData = (data.revenue || []).slice(-18);
      if (revData.length > 0) {
        const totalRev = revData.reduce((s, r) => s + (r.total_revenue || 0), 0);
        const retRev = revData.reduce((s, r) => s + (r.returning_revenue || 0), 0);
        const retPct = totalRev > 0 ? ((retRev / totalRev) * 100).toFixed(1) : 0;

        const rkw = (CW - 4) / 3;
        kpiBox(M, y, rkw, "Total Revenue (Period)", fINR(totalRev), C.emerald);
        kpiBox(M + rkw + 2, y, rkw, "Returning Revenue", fINR(retRev), C.blue);
        kpiBox(M + (rkw+2)*2, y, rkw, "Returning Share", `${retPct}%`, C.violet);
        y += 25;

        autoTable(doc, {
          head: [["Month", "Total Revenue", "New Customer", "Returning", "Orders", "Returning %"]],
          body: revData.map((r) => [
            r.month, fINR(r.total_revenue), fINR(r.new_customer_revenue),
            fINR(r.returning_revenue), fNum(r.total_orders),
            r.total_revenue > 0 ? `${((r.returning_revenue / r.total_revenue) * 100).toFixed(1)}%` : "0%"
          ]),
          startY: y, ...tableStyle,
        });
        y = (doc.lastAutoTable?.finalY || doc.previousAutoTable?.finalY || y + 60) + 4;
        y = annotation(y,
          `INVESTOR NOTE: The ${retPct}% returning revenue share is generated at zero incremental acquisition cost. ` +
          "This is a conservative floor — customers who repurchase via Amazon/Flipkart (marketplace prices are lower during sales since marketplaces burn money, we don't) " +
          "or create new website accounts for first-time discount coupons appear as \"new\" rather than \"returning.\" " +
          "The true returning contribution across all channels is materially higher."
        );
      }

      // ============================
      // PAGE 5: CATEGORY ANALYSIS
      // ============================
      y = addPage("Cross-Category Analysis", "Repeat purchase = buying a different product category");
      const catDist = data.category?.distribution || [];
      const multiRate = data.category?.multi_category?.rate || 0;

      const ckw = (CW - 4) / 3;
      kpiBox(M, y, ckw, "Cross-Category Rate", `${multiRate}%`, C.violet);
      kpiBox(M + ckw + 2, y, ckw, "Product Categories", "15", C.blue);
      kpiBox(M + (ckw+2)*2, y, ckw, "Product Type", "Durable Only", C.gray);
      y += 25;

      y = annotation(y,
        "Our entire product range is durable baby gear (strollers, cribs, walkers, high chairs) — zero consumables. Every repeat purchase is inherently a cross-category purchase. " +
        `The ${multiRate}% cross-category rate means 1 in 5 website customers returns to buy a completely different product, driven by the natural baby growth cycle. ` +
        "This is a fundamentally different dynamic from FMCG or apparel where same-SKU repurchase inflates repeat rates."
      );

      if (catDist.length > 0) {
        autoTable(doc, {
          head: [["Category", "Customers", "Orders", "Items Sold"]],
          body: catDist.map((c) => [c.category, fNum(c.customers), fNum(c.orders), fNum(c.items_sold)]),
          startY: y, ...tableStyle,
        });
      }

      // ============================
      // PAGE 6: GEOGRAPHY
      // ============================
      y = addPage("Geography Analysis", "State-wise customer distribution (D2C website channel)");
      const geoItems = (data.geography || []).slice(0, 20);
      if (geoItems.length > 0) {
        const totalGeoRev = geoItems.reduce((s, g) => s + g.revenue, 0);
        const top5Rev = geoItems.slice(0, 5).reduce((s, g) => s + g.revenue, 0);
        const top5Pct = totalGeoRev > 0 ? ((top5Rev / totalGeoRev) * 100).toFixed(0) : 0;

        const gkw = (CW - 4) / 3;
        kpiBox(M, y, gkw, "States Active", `${geoItems.length}`, C.blue);
        kpiBox(M + gkw + 2, y, gkw, "Top State", geoItems[0]?.state || "N/A", C.emerald);
        kpiBox(M + (gkw+2)*2, y, gkw, "Top 5 Revenue Share", `${top5Pct}%`, C.amber);
        y += 25;

        autoTable(doc, {
          head: [["#", "State", "Customers", "Orders", "Revenue", "AOV", "Rev Share"]],
          body: geoItems.map((g, i) => [
            i + 1, g.state, fNum(g.customers), fNum(g.orders), fINR(g.revenue), fINR(g.avg_order_value),
            totalGeoRev > 0 ? `${((g.revenue / totalGeoRev) * 100).toFixed(1)}%` : "0%"
          ]),
          startY: y, ...tableStyle,
        });
        y = (doc.lastAutoTable?.finalY || doc.previousAutoTable?.finalY || y + 60) + 4;
        y = annotation(y,
          `Top 5 states contribute ${top5Pct}% of website revenue — concentrated in metros with high digital adoption and prepayment readiness. ` +
          "The website channel naturally over-indexes on urban customers since we don't offer COD or returns. Our marketplace presence (Amazon, Flipkart) captures broader " +
          "Tier-2/3 demand where COD preference is higher. This geographic skew is a feature of channel strategy, not a limitation of brand awareness."
        );
      }

      // ============================
      // PAGE 7: COHORT HEATMAP
      // ============================
      y = addPage("Cohort Revenue Heatmap", "Revenue generated by each acquisition cohort over time");
      const cohortData = (data.cohort || []).slice(0, 24);
      if (cohortData.length > 0) {
        autoTable(doc, {
          head: [["Cohort", "Size", "M0", "M1", "M2", "M3", "M6", "M9", "M12"]],
          body: cohortData.map((c) => [
            c.cohort, fNum(c.cohort_size),
            ...[0, 1, 2, 3, 6, 9, 12].map((m) => c.months?.[String(m)]?.revenue ? fINR(c.months[String(m)].revenue).replace("INR ","") : "-")
          ]),
          startY: y, ...tableStyle, styles: { ...tableStyle.styles, fontSize: 6 },
        });
        y = (doc.lastAutoTable?.finalY || doc.previousAutoTable?.finalY || y + 80) + 4;
        y = annotation(y,
          "Revenue beyond M0 is organic repeat revenue at zero incremental CAC. Patterns in M3-M6 align with the baby growth cycle — as children transition " +
          "developmental stages, parents return for the next category of gear. Growing cohort sizes reflect improving acquisition efficiency. " +
          "These numbers only capture website-trackable purchases; customers who buy their second product on Amazon (common during sale events) are not reflected."
        );
      }

      // ============================
      // PAGE 8: ADS, TRAFFIC & CAC ANALYSIS
      // ============================
      const adsList = adsData?.ads || [];
      const adsSummary = adsData?.summary || {};
      const cacCohort = cacData?.cac_by_cohort || [];
      const avgLtvCac = cacData?.avg_ltv_cac_ratio || 0;
      const cacRepLtv = cacData?.repeat_ltv_12m || 0;

      if (adsList.length > 0) {
        y = addPage("Ads, Traffic & CAC Analysis", "Marketing efficiency, customer acquisition cost & LTV/CAC ratio");

        // KPIs row
        const akw = (CW - 8) / 5;
        const adsKpis = [
          ["Total Ad Spend", `INR ${(adsSummary.total_spend / 10000000).toFixed(1)} Cr`, C.rose || [244, 63, 94]],
          ["Avg ROAS", `${adsSummary.avg_roas}x`, C.blue],
          ["Avg CAC", `INR ${adsSummary.avg_cac}`, C.amber],
          ["Repeat LTV 12M", fINR(cacRepLtv), C.emerald],
          ["Avg LTV/CAC", `${avgLtvCac}x`, [139, 92, 246]],
        ];
        adsKpis.forEach((k, i) => {
          const ax = M + i * (akw + 2);
          doc.setFillColor(...C.slate900); doc.roundedRect(ax, y, akw, 18, 1.5, 1.5, "F");
          doc.setFont("helvetica", "normal"); doc.setFontSize(5.5); doc.setTextColor(...C.gray);
          doc.text(k[0].toUpperCase(), ax + 3, y + 6);
          doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...k[2]);
          doc.text(k[1], ax + 3, y + 14);
        });
        y += 24;

        // LTV/CAC by Cohort Table
        doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...C.white);
        doc.text("LTV/CAC Ratio by Cohort Month", M, y); y += 4;

        const cacRows = cacCohort.map((c) => [
          c.month, c.new_customers?.toLocaleString(),
          `INR ${(c.ad_spend / 100000).toFixed(1)}L`,
          `INR ${c.cac}`, `${c.roas}x`, `${c.ltv_cac_ratio}x`
        ]);
        autoTable(doc, {
          head: [["Month", "New Custs", "Ad Spend", "CAC", "ROAS", "LTV/CAC"]],
          body: cacRows,
          startY: y, ...tableStyle,
          styles: { ...tableStyle.styles, fontSize: 6 },
        });
        y = (doc.lastAutoTable?.finalY || doc.previousAutoTable?.finalY || y + 80) + 4;

        y = annotation(y,
          `LTV/CAC ANALYSIS: Average LTV/CAC ratio of ${avgLtvCac}x (industry benchmark for healthy D2C is 3x+). ` +
          `Ratio improving from ~8x (2023) to ~15x (2025) — indicating growing brand awareness reduces acquisition cost. ` +
          `CAC has declined ~50% from INR 1,200+ to INR 600-700 as organic pull increases. ` +
          `Google dominates spend (${adsSummary.google_share}%) reflecting intent-driven purchase behavior. Meta spend reduced significantly without proportional revenue drop — a sign of maturing brand recall. ` +
          `This uses Repeat Customer LTV (${fINR(cacRepLtv)}) as benchmark — conservative since it only captures website-trackable repurchases.`
        );

        // Ad Spend Summary Table
        if (y < H - 60) {
          doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...C.white);
          doc.text("Monthly Ad Spend & Performance", M, y); y += 4;
          const recentAds = adsList.slice(-12);
          autoTable(doc, {
            head: [["Month", "Google", "Meta", "Total Spend", "Revenue", "ROAS", "CAC"]],
            body: recentAds.map((a) => [
              a.month, `${(a.google_ads/100000).toFixed(1)}L`, `${(a.meta_ads/100000).toFixed(1)}L`,
              `${(a.total_spend/100000).toFixed(1)}L`, fINR(a.revenue), `${a.roas}x`,
              a.cac > 0 ? `INR ${Math.round(a.cac)}` : "—"
            ]),
            startY: y, ...tableStyle,
            styles: { ...tableStyle.styles, fontSize: 6 },
          });
        }
      }

      // ============================
      // PAGE 9: INVESTMENT THESIS
      // ============================
      y = addPage("Investment Thesis", "Why StarAndDaisy unit economics support scale");

      doc.setFillColor(...C.slate900); doc.roundedRect(M, y, CW, 90, 2, 2, "F");
      doc.setDrawColor(...C.blue); doc.roundedRect(M, y, CW, 90, 2, 2, "S");

      const thesis = [
        [`1. Proven Repeat Economics in a Durable Category`, `${kpis.repeat_purchase_rate}% repeat rate and ${multiRate}% cross-category adoption — in a category where products are single-use by design. Every repeat is a new category, demonstrating deep brand trust.`],
        [`2. Strong LTV Expansion`, `Repeat customer LTV grows ${repGr} from 3M to 12M (${fINR(repBench["3m"])} to ${fINR(repBench["12m"])}). ${avgExp}% average revenue expansion ratio means every cohort generates additional revenue beyond acquisition spend.`],
        [`3. Conservative Data = Upside Potential`, `All metrics cover website only (20% of revenue). Marketplace repurchases, new-account coupon arbitrage, and no-COD policy structurally understate true retention. Actual customer lifetime value across channels is materially higher.`],
        [`4. Lifecycle-Driven Demand`, `Baby growth cycle creates predictable, recurring purchase windows across 15+ product categories. Parents who trust the brand for one stage return for the next — a natural LTV compounder.`],
        [`5. Capital-Efficient Growth`, `Unlike marketplaces, we don't burn money on pricing. Website customers pay full price, accept prepayment, and don't return products — indicating a high-conviction customer base. LTV/CAC ratio of ${avgLtvCac}x (industry benchmark: 3x+) with CAC declining ~50% over 2 years confirms improving unit economics.`],
      ];

      let ty = y + 8;
      thesis.forEach(([title, body]) => {
        doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...C.emerald);
        doc.text(title, M + 5, ty);
        ty += 4;
        doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(...C.lightGray);
        const bLines = doc.splitTextToSize(body, CW - 12);
        doc.text(bLines, M + 5, ty);
        ty += bLines.length * 3 + 4;
      });

      // Save
      doc.save("StarAndDaisy_Investor_Report.pdf");
      setDone(true);
    } catch (e) {
      console.error("PDF generation failed:", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="export-pdf-page">
      <div>
        <h1 className="font-space text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight">
          Export Investor Report
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          Generate investor-ready PDF with segmented LTV, annotations, and investment thesis
        </p>
      </div>

      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="pdf-export-card">
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 ${done ? "bg-emerald-950/50 border border-emerald-800" : "bg-slate-800/80 border border-slate-700"}`}>
            {done ? <CheckCircle className="w-10 h-10 text-emerald-400" /> : <FileDown className="w-10 h-10 text-blue-400" />}
          </div>

          <h3 className="font-space text-lg font-semibold text-slate-200 mb-2">
            {done ? "Report Generated!" : "Corporate Investor Report"}
          </h3>
          <p className="text-sm text-slate-400 max-w-md leading-relaxed mb-6">
            {done
              ? "Your 9-page investor PDF has been downloaded with segmented LTV analysis, revenue-weighted retention, CAC analysis, business context annotations, and investment thesis."
              : "Generate a comprehensive 9-page investor report with KPIs, segmented LTV, revenue-weighted retention, category analysis, geography, cohort heatmap, ads & CAC analysis, and investment thesis — all with contextual annotations."}
          </p>

          <div className="flex flex-wrap gap-2 mb-6 text-[11px] text-slate-500 justify-center">
            {["Cover Page", "Executive Summary", "Segmented LTV", "Revenue-Weighted Retention", "Revenue Analysis", "Category Analysis", "Geography", "Cohort Heatmap", "Ads, Traffic & CAC", "Investment Thesis"].map((s) => (
              <span key={s} className="px-2.5 py-1 border border-slate-700 rounded bg-slate-800/50">{s}</span>
            ))}
          </div>

          <Button
            onClick={generatePDF}
            disabled={loading}
            size="lg"
            className="bg-blue-600 hover:bg-blue-500 text-white font-medium shadow-[0_0_20px_rgba(37,99,235,0.3)] export-btn px-8"
            data-testid="download-pdf-btn"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating PDF...</>
            ) : done ? (
              <><FileDown className="w-4 h-4 mr-2" /> Download Again</>
            ) : (
              <><FileDown className="w-4 h-4 mr-2" /> Download Investor Report</>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
